/**
 * PackagesContext module.
 * Provides available packages and default package ID to all apps.
 * Extracted from SaleContext to prevent unnecessary re-renders in selfcare
 * components that only need package data.
 *
 * @package Wecom
 */
// External packages
import { createContext, useContext, useState, useEffect, useMemo } from "react";

// Relative imports - API
import { getPackages } from "../api/public/getPackagesApi";

const PackagesContext = createContext(null);

/**
 * PackagesProvider component.
 * Fetches available packages on mount and provides them via context.
 *
 * @component
 * @param {Object} props - Component props.
 * @param {React.ReactNode} props.children - Child components.
 * @returns {JSX.Element} The provider with packages context value.
 */
export function PackagesProvider({ children }) {
  const [packages, setPackages] = useState([]);
  const [defaultPackageId, setDefaultPackageId] = useState(null);

  // Fetch packages once on mount.
  useEffect(() => {
    let isMounted = true;

    const fetchPackages = async () => {
      try {
        const res = await getPackages();
        if (
          isMounted &&
          res &&
          res.status === 1 &&
          Array.isArray(res.data?.data)
        ) {
          setPackages(res.data.data);
          // Extract default_package_id from API response
          const defaultId = res.data?.default_package_id ?? null;
          setDefaultPackageId(defaultId);
        }
      } catch (_error) {
        // Silently handle package fetch errors
      }
    };

    fetchPackages();

    return () => {
      isMounted = false;
    };
  }, []);

  const value = useMemo(
    () => ({
      packages,
      defaultPackageId,
    }),
    [packages, defaultPackageId]
  );

  return (
    <PackagesContext.Provider value={value}>
      {children}
    </PackagesContext.Provider>
  );
}

/**
 * Custom hook to access the PackagesContext value.
 *
 * @returns {Object} Packages context value.
 */
export function usePackagesContext() {
  const context = useContext(PackagesContext);
  if (context === undefined || context === null) {
    throw new Error("usePackagesContext must be used within a PackagesProvider");
  }
  return context;
}
